import { reportClientError } from '@/lib/reportError';
import { useState, useEffect, useCallback, useRef } from 'react';
import { getStoredAffiliateRef } from '@/lib/marketing';

export type SubscriptionTier = 'day_pass' | 'monthly' | 'yearly' | 'creator' | 'reader';

export interface Subscription {
  status: 'active' | 'canceled' | 'past_due' | 'expired' | 'none';
  isSubscriber: boolean;
  // Real tool entitlement. Free Creator/Reader tiers are isSubscriber:true
  // (status active) but canUseTools:false — gate paid tools on THIS.
  canUseTools?: boolean;
  tier?: SubscriptionTier;
  tierName?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  dayPassExpiresAt?: string;
  cancelAtPeriodEnd: boolean;
  readerSessionsUsed: number;
  readerSessionsRemaining: number;
  readerSessionsLimit: number;
  readerSessionsResetDate?: string;
  canBeReader?: boolean;
  canSellProducts?: boolean;
  creditsIncluded?: number;
  isUnlimited?: boolean; // team/admin unlimited accounts — never upsell these
  // Charter (prepaid) terms only: what happens when the term ends.
  charterRenewal?: {
    endsAt: string;
    renewsAtCents: number;
    optOut: boolean;
    card: { brand: string; last4: string } | null;
  };
}

export const SUBSCRIPTION_TIERS = {
  day_pass: {
    id: 'day_pass' as SubscriptionTier,
    name: 'Day Pass',
    price: 5,
    priceDisplay: '$5',
    period: '24 hours',
    description: 'Perfect for last-minute video edits or polishing a single audition.',
    features: [
      'Full access to the video tools for 24 hours',
      '1 reader booking',
      '2 video tokens (yours to keep)'
    ],
    credits: 2,
    readerSessions: 1,
    canBeReader: true
  },
  monthly: {
    id: 'monthly' as SubscriptionTier,
    name: 'Hey Joe Plus',
    price: 9.99,
    priceDisplay: '$9.99',
    period: 'month',
    description: 'Member pricing across the marketplace, your replay library, reader bookings, and the full video toolset.',
    features: [
      'Member pricing across the marketplace, including classes that are free for members',
      'Replay library: events you register for stay yours while you\'re a member',
      'A $25 Ticket Rewards code to claim every 4 months',
      'Full access to the video tools, 10 video tokens a month',
      '5 reader bookings/month'
    ],
    credits: 10,
    readerSessions: 5,
    canBeReader: true
  },
  yearly: {
    id: 'yearly' as SubscriptionTier,
    name: 'Hey Joe Plus (Yearly)',
    price: 99,
    priceDisplay: '$99',
    period: 'year',
    monthlyEquivalent: '$8.25/mo',
    description: 'Everything in Hey Joe Plus, two months cheaper, plus Hey Joe credit when you join and a free class every year.',
    features: [
      'Everything in Hey Joe Plus',
      '$25 or more in Hey Joe credit when you join, for events and classes',
      'One free class a year on top of member pricing',
      'Member pricing across the marketplace, including classes that are free for members',
      'Replay library, Ticket Rewards, video tools and 5 reader bookings/month'
    ],
    credits: 10,
    readerSessions: 5,
    canBeReader: true
  },
  creator: {
    id: 'creator' as SubscriptionTier,
    name: 'Hey Joe Creator',
    price: 0,
    priceDisplay: 'Free',
    period: '',
    description: 'Sell your courses and content on the marketplace. No monthly fee — we take a small commission on each sale.',
    features: [
      'Sell courses & content on the marketplace',
      'Create your own store page',
      'Register as a reader',
      'Get discovered by buyers',
      'No monthly fee — commission only'
    ],
    credits: 0,
    readerSessions: 0,
    canBeReader: true,
    canSellProducts: true
  },
  reader: {
    id: 'reader' as SubscriptionTier,
    name: 'Hey Joe Creator',
    price: 0,
    priceDisplay: 'Free',
    period: '',
    description: 'Sell courses and content and register as a reader on the marketplace. No monthly fee, commission only. The video tools are Hey Joe Plus.',
    features: [
      'Everything in Hey Joe Plus',
      'Register as a reader',
      'Offer paid reading services',
      'Get discovered in marketplace'
    ],
    credits: 10,
    readerSessions: 5,
    canBeReader: true
  }
} as const;

export function useSubscription() {
  // Hydrate from the last-known subscription so nav items gated on it (My
  // Store etc.) don't vanish during every fetch window or transient failure.
  // Write-through on success; cleared on real 401 or logout.
  const [subscription, setSubscription] = useState<Subscription | null>(() => {
    try {
      if (!(localStorage.getItem('authToken') || localStorage.getItem('token'))) return null;
      const cached = localStorage.getItem('subscription_cache');
      return cached ? (JSON.parse(cached) as Subscription) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(() => {
    // Start loading if user has a token — prevents false negatives on first render
    return !!(localStorage.getItem('authToken') || localStorage.getItem('token'));
  });
  const [error, setError] = useState<string | null>(null);

  const getAuthToken = useCallback(() => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  }, []);

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  }, [getAuthToken]);

  // Fetch user's subscription status
  const fetchSubscription = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setSubscription(null);
      try { localStorage.removeItem('subscription_cache'); } catch { /* noop */ }
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/subscription', {
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          setSubscription(null);
          try { localStorage.removeItem('subscription_cache'); } catch { /* noop */ }
          return null;
        }
        // Non-auth failure: keep the last-known subscription rather than
        // flashing the logged-out/free state.
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();
      setSubscription(data);
      try { localStorage.setItem('subscription_cache', JSON.stringify(data)); } catch { /* noop */ }
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getHeaders]);

  // Create subscription checkout session (redirects to Stripe)
  //
  // The in-flight ref is the double-click guard. setLoading(true) below can't do
  // that job — it's async state, so two clicks a few hundred ms apart both get
  // through and each mints a fresh Stripe session. One person produced three
  // sessions in ninety seconds on Sep 13, which made the abandonment numbers
  // look far worse than reality and left two orphan sessions behind. A ref
  // updates synchronously, and living in the hook means every caller is covered
  // whether or not its own button disables.
  const checkoutInFlight = useRef(false);
  const createCheckout = useCallback(async (tier: SubscriptionTier = 'monthly', returnTo?: string, campaign?: string, campaignProductId?: string): Promise<string | null> => {
    if (checkoutInFlight.current) return null;
    const token = getAuthToken();
    if (!token) {
      setError('Authentication required');
      return null;
    }

    checkoutInFlight.current = true;
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/subscription/create', {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        // The first-touch referral code (?ref=, kept in localStorage). Only the Charter
        // page used to send it, so a Plus signup through any referral link credited
        // nobody. The server ignores a code it can't resolve, and self-referral.
        body: JSON.stringify({ tier, ...(returnTo ? { returnTo } : {}), ...(campaign ? { campaign } : {}), ...(campaignProductId ? { campaignProductId } : {}), ...(getStoredAffiliateRef() ? { ref: getStoredAffiliateRef() } : {}) }),
      });

      if (!response.ok) {
        const data = await response.json();
        reportClientError('checkout_failed', data.code || data.error || String(response.status), { tier, status: response.status });
        if (data.code === 'ALREADY_SUBSCRIBED') {
          setError('You already have an active subscription');
          return null;
        }
        throw new Error(data.error || 'Failed to create checkout');
      }

      const data = await response.json();
      return data.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setLoading(false);
      checkoutInFlight.current = false;
    }
  }, [getAuthToken, getHeaders]);

  // Cancel subscription (at period end)
  const cancelSubscription = useCallback(async (): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) {
      setError('Authentication required');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      // Refresh subscription data
      await fetchSubscription();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getHeaders, fetchSubscription]);

  // Reactivate canceled subscription
  const reactivateSubscription = useCallback(async (): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) {
      setError('Authentication required');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/subscription/reactivate', {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reactivate subscription');
      }

      // Refresh subscription data
      await fetchSubscription();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getHeaders, fetchSubscription]);

  // Get Stripe customer portal URL
  const getPortalUrl = useCallback(async (): Promise<string | null> => {
    const token = getAuthToken();
    if (!token) {
      setError('Authentication required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/subscription/portal', {
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to get portal URL');
      }

      const data = await response.json();
      return data.portalUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, getHeaders]);

  // Open Stripe customer portal in new tab
  const openPortal = useCallback(async () => {
    const url = await getPortalUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }, [getPortalUrl]);

  // Start subscription flow for a specific tier
  const startSubscription = useCallback(async (tier: SubscriptionTier = 'monthly', returnTo?: string, campaign?: string, campaignProductId?: string) => {
    const url = await createCheckout(tier, returnTo, campaign, campaignProductId);
    if (url) {
      window.location.href = url;
    }
  }, [createCheckout]);

  // Activate the FREE Creator tier — no Stripe checkout, just grants selling.
  // Returns true on success. Refreshes the subscription so the UI reflects it.
  const activateCreator = useCallback(async (): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) return false;
    try {
      const res = await fetch('/api/creator/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      await fetchSubscription();
      return true;
    } catch {
      return false;
    }
  }, [getAuthToken, fetchSubscription]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!getAuthToken();
  }, [getAuthToken]);

  // Format subscription end date
  const getEndDateDisplay = useCallback(() => {
    // For day pass, show expiration time
    if (subscription?.tier === 'day_pass' && subscription?.dayPassExpiresAt) {
      const date = new Date(subscription.dayPassExpiresAt);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }

    if (!subscription?.currentPeriodEnd) return null;
    const date = new Date(subscription.currentPeriodEnd);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, [subscription]);

  // Get subscription status display
  const getStatusDisplay = useCallback(() => {
    if (!subscription) return 'Not Subscribed';

    switch (subscription.status) {
      case 'active':
        if (subscription.tier === 'day_pass') {
          return 'Day Pass Active';
        }
        if (subscription.cancelAtPeriodEnd) {
          return `Canceling ${getEndDateDisplay()}`;
        }
        return 'Active';
      case 'expired':
        return 'Expired';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      default:
        return 'Not Subscribed';
    }
  }, [subscription, getEndDateDisplay]);

  // Get tier display name
  const getTierDisplay = useCallback(() => {
    if (!subscription?.tier) return null;
    return SUBSCRIPTION_TIERS[subscription.tier]?.name || subscription.tierName;
  }, [subscription]);

  // Refresh subscription on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated()) {
      fetchSubscription();
    }
  }, [fetchSubscription, isAuthenticated]);

  // Listen for subscription success from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('subscription') === 'success') {
      // Refresh subscription data after successful checkout
      fetchSubscription();
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [fetchSubscription]);

  return {
    // State
    subscription,
    loading,
    error,

    // Actions
    fetchSubscription,
    createCheckout,
    startSubscription,
    activateCreator,
    cancelSubscription,
    reactivateSubscription,
    getPortalUrl,
    openPortal,

    // Helpers
    getStatusDisplay,
    getEndDateDisplay,
    getTierDisplay,
    isAuthenticated,

    // Computed values
    isSubscriber: subscription?.isSubscriber ?? false,
    // Real paid-tool entitlement (free Creator/Reader tiers are isSubscriber:true
    // but canUseTools:false). Unlimited/admin accounts report canUseTools:true.
    canUseTools: subscription?.canUseTools ?? subscription?.isUnlimited ?? false,
    status: subscription?.status ?? 'none',
    tier: subscription?.tier,
    tierName: subscription?.tierName,
    canBeReader: subscription?.canBeReader ?? false,
    canSellProducts: subscription?.canSellProducts ?? false,
    readerSessionsRemaining: subscription?.readerSessionsRemaining ?? 0,
    readerSessionsUsed: subscription?.readerSessionsUsed ?? 0,
    readerSessionsLimit: subscription?.readerSessionsLimit ?? 0,
    creditsIncluded: subscription?.creditsIncluded ?? 0,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd ?? false,
    isDayPass: subscription?.tier === 'day_pass',
  };
}

// Legacy export for backward compatibility
export const SUBSCRIPTION_PRICE = {
  monthly: 9.99,
  currency: 'USD',
  display: '$9.99/month'
} as const;
